// src/components/imagery/TimeStrip/timeStripModel.js
//
// The time strip's rules, as functions (build item 9, step 4; batch-lane
// design §5.1). The strip itself and each adapter render what these decide.
//
// TWO LANES, NEVER ONE LIST. The lanes come from the server's
// listEpochsForPoint (GET /api/v1/features/recheck/items/:id), already split by
// product class and filtered to the viewer's entitlements:
//   collects   the 10 cm visual collects, oldest first. A verdict is given on one
//              of these, and only on the item's own.
//   reference  Sentinel-2 annual composites, oldest first. Context only: never
//              a verdict's basis (migration 133).
// derived (indices) is in neither, by the server's query.

/** "2025_12_13" -> "2025-12-13". Anything else is shown as given. */
export function formatEpoch(acquisitionDate) {
  if (typeof acquisitionDate !== "string") return "";
  const m = /^(\d{4})_(\d{2})(?:_(\d{2}))?$/.exec(acquisitionDate);
  if (!m) return acquisitionDate;
  return m[3] ? `${m[1]}-${m[2]}-${m[3]}` : `${m[1]}-${m[2]}`;
}

const EMPTY = Object.freeze({ collects: [], reference: [] });

/** What an empty collects lane says when the adapter knows no more. */
export const COLLECTS_EMPTY_TEXT = "No collect you can view covers this point.";

/** The lanes as given, or two empty lanes. Never merged. */
export function normalizeLanes(lanes) {
  if (!lanes) return EMPTY;
  return {
    collects: Array.isArray(lanes.collects) ? lanes.collects : [],
    reference: Array.isArray(lanes.reference) ? lanes.reference : [],
  };
}

/** Which lane holds this dataset key: "collects", "reference" or null. */
export function laneOf(lanes, datasetKey) {
  const l = normalizeLanes(lanes);
  if (!datasetKey) return null;
  if (l.collects.some((e) => e.dataset_key === datasetKey)) return "collects";
  if (l.reference.some((e) => e.dataset_key === datasetKey)) return "reference";
  return null;
}

/**
 * Where the cursor starts for an item: its own epoch, if the viewer's collects
 * hold it. null otherwise (not entitled, or not covering): the strip then has
 * no cursor, and nothing can be judged.
 */
export function openingCursor(lanes, itemDatasetKey) {
  return laneOf(lanes, itemDatasetKey) === "collects" ? itemDatasetKey : null;
}

/**
 * The compare epoch the strip offers first: the server's (the approved
 * record's epoch), if it is one of the viewer's collects and not the item's
 * own. null otherwise.
 */
export function defaultCompare(lanes, itemDatasetKey, serverCompareKey) {
  if (!serverCompareKey || serverCompareKey === itemDatasetKey) return null;
  return laneOf(lanes, serverCompareKey) === "collects" ? serverCompareKey : null;
}

export const GATE_MESSAGES = Object.freeze({
  no_epoch: "This item's epoch is not available to you here, so it cannot be judged.",
  reference: "Sentinel-2 is context only. Move the cursor to the item's collect to give a verdict.",
  other_epoch: "The verdict is about the item's own collect. Move the cursor back to it.",
});

/**
 * May a verdict be given with the cursor here? Only on the item's own collect,
 * which is also what the server checks (422 wrong_epoch).
 *
 * @returns {{allowed: boolean, reason: null|"no_epoch"|"reference"|"other_epoch", message: string|null}}
 */
export function verdictGate({ lanes, cursorKey, itemDatasetKey }) {
  const own = openingCursor(lanes, itemDatasetKey);
  const no = (reason) => ({ allowed: false, reason, message: GATE_MESSAGES[reason] });
  if (!own) return no("no_epoch");
  const lane = laneOf(lanes, cursorKey);
  if (lane === "reference") return no("reference");
  if (cursorKey !== own) return no("other_epoch");
  return { allowed: true, reason: null, message: null };
}
